import React, { FC, useMemo, useState } from "react";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { useTransactionHistory } from "@/hooks/useTransactionHistory";
import DataTable, { HeadCell, DataTableCell } from "@/components/DataTable";
import { createDataRow } from "@/components/DataTable/createDataRow";
import { useCurrencySetting } from "@/hooks/useSetting";
import {
  formatTransactionAmount,
  formatTransactionTimestamp,
  DisplayTransaction,
} from "@/utils/transaction.utils";
import { Typography } from "@mui/material";

type TransactionHistoryModalProps = {
  open: boolean;
  onClose: () => void;
  participantId: string;
  userId: string;
};

const TransactionHistoryModal: FC<TransactionHistoryModalProps> = ({
  open,
  onClose,
  participantId,
  userId,
}) => {
  const { t } = useTranslation("clients");
  const theme = useAppTheme();
  const currency = useCurrencySetting();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = useState<string>("timestamp");

  // Fetch transaction history
  const { transactions, isLoading, isError } = useTransactionHistory({
    participantId,
    userId,
    enabled: open,
  });

  // Define table columns
  const headCells: HeadCell[] = [
    {
      id: "timestamp",
      disablePadding: false,
      isSortable: true,
      label: t("client_profile.transaction_timestamp"),
      width: "35%",
    },
    {
      id: "amount",
      disablePadding: false,
      isSortable: false,
      label: t("client_profile.transaction_amount"),
      width: "30%",
      align: "right",
    },
    {
      id: "event",
      disablePadding: false,
      isSortable: false,
      label: t("client_profile.transaction_event"),
      width: "35%",
      align: "left",
    },
  ];

  // Transform transactions to table rows
  const tableData: DataTableCell[] = useMemo(() => {
    if (!transactions || transactions.length === 0) return [];

    // Sort data based on current sort configuration
    const sortedTransactions = [...transactions].sort((a, b) => {
      if (orderBy === "timestamp") {
        const comparison =
          new Date(a.originalDate).getTime() -
          new Date(b.originalDate).getTime();
        return order === "asc" ? comparison : -comparison;
      }
      if (orderBy === "amount") {
        const comparison = a.amount - b.amount;
        return order === "asc" ? comparison : -comparison;
      }
      if (orderBy === "event") {
        const comparison = a.event.localeCompare(b.event);
        return order === "asc" ? comparison : -comparison;
      }
      return 0;
    });

    return sortedTransactions.map((transaction: DisplayTransaction) =>
      createDataRow(transaction.id, {
        timestamp: formatTransactionTimestamp(transaction.timestamp),
        amount: formatTransactionAmount(transaction.amount, currency.currency),
        event: transaction.event,
      }),
    );
  }, [transactions, order, orderBy, currency]);

  // Client-side pagination: slice data for current page
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return tableData.slice(startIndex, endIndex);
  }, [tableData, page, rowsPerPage]);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeSort = (
    newOrder: string,
    newOrderBy: keyof DataTableCell,
  ) => {
    setOrder(newOrder as "asc" | "desc");
    setOrderBy(String(newOrderBy));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={false}
      theme={theme}
      title={t("client_profile.transactions_history")}
      size={{
        width: "100%",
        minHeight: 540,
        maxHeight: 540,
        maxWidth: 650,
      }}
      content={
        <Box sx={{ width: "100%", minHeight: "400px" }}>
          {isError ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "400px",
              }}
            >
              <Typography variant="bodyLarge" color="text.secondary">
                {t("client_profile.transaction_error")}
              </Typography>
            </Box>
          ) : (
            <DataTable
              data={paginatedData}
              headCells={headCells}
              rowsPerPage={rowsPerPage}
              page={page}
              total={tableData.length}
              order={order}
              orderBy={orderBy}
              isLoading={isLoading}
              messageEmptyData={t("client_profile.no_transactions_yet")}
              showPagination={true}
              onChangePage={handleChangePage}
              onChangeSize={handleChangeRowsPerPage}
              onChangeSort={handleChangeSort}
              headCellFontSize="14px"
              emptyDataFontSize="14px"
              bodyCellFontSize="14px"
            />
          )}
        </Box>
      }
    />
  );
};

export default TransactionHistoryModal;
